import type { PImage } from '../../engine/aliases'
import type { IDeadish, IEntity } from '../../entities/entitiesUtils'
import { entitiesUtils } from '../../entities/entitiesUtils-sidecar'
import { angleUtil } from '../../lib/angleUtil-sidecar'
import { l } from '../../lib/imports'
import { mathUtils } from '../../lib/mathUtils-sidecar'
import { rando } from '../../lib/rando-sidecar'
import { createId } from '../../main/common'
import { sprites } from '../../sprites/sprites-sidecar'
import { spritesUtil } from '../../sprites/spritesUtil-sidecar'
import {
  bodies,
  clothesColors,
  hairColors,
  hairs,
  heads,
  IBodyData,
  IHairData,
  IHeadData,
  skinTones,
} from './voyageBodiesData'
import { voyageLayers } from './voyageLayers-sidecar'

export interface IVoyageBody extends IEntity, IDeadish {
  imageBody: PImage
  imageHead: PImage
  imageHair: PImage

  x: number
  y: number
  isFlipped: boolean

  head: IHeadData
  body: IBodyData
  hair: IHairData

  scale: number

  bodyColor: number
  hairColor: number
  headColor: number

  rotation: number
}

const objs: IVoyageBody[] = []
export function getAll() {
  return objs
}

const _masterScale = 0.25

export function create(): IVoyageBody {
  let body = rando.sample(bodies)
  let head = rando.sample(heads)
  let hair = rando.sample(hairs)

  let imageBody = spritesUtil.createImage(
    voyageLayers.getLayers().shipCrew,
    body.frames[0],
  )
  let _scale = _masterScale

  let bodyColor = rando.sample(clothesColors)
  imageBody.tint = bodyColor

  let imageHead = spritesUtil.createImage(
    voyageLayers.getLayers().shipCrew,
    head.frames[0],
  )

  let headColor = rando.sample(skinTones)
  imageHead.tint = headColor

  let imageHair = spritesUtil.createImage(
    voyageLayers.getLayers().shipCrew,
    hair.frames[0] || sprites.voyHairBuzz[0],
  )

  let hairColor = rando.sample(hairColors)

  if (!hair.frames[0]) {
    imageHair.visible = false
  } else {
    imageHair.tint = hairColor
  }

  let id = createId()

  let obj: IVoyageBody = {
    id,
    idLoaded: -1,
    isDead: false,
    eType: 'voy-misc',
    imageBody,
    imageHead,
    imageHair,
    x: 0,
    y: 0,
    isFlipped: false,
    body,
    head,
    hair,
    scale: _scale,
    bodyColor,
    hairColor,
    headColor,
    rotation: 0,
  }
  objs.push(obj)

  scale(obj, _scale)

  return obj
}

export function setBody(obj: IVoyageBody, d: IBodyData) {
  obj.body = d
  spritesUtil.setPackedFrame(obj.imageBody, obj.body.frames[0])
  scale(obj, obj.scale)
}
export function setHead(obj: IVoyageBody, d: IHeadData) {
  obj.head = d
  spritesUtil.setPackedFrame(obj.imageHead, obj.head.frames[0])
  scale(obj, obj.scale)
}
export function setHair(obj: IVoyageBody, d: IHairData) {
  obj.hair = d
  if (obj.hair.frames[0]) {
    spritesUtil.setPackedFrame(obj.imageHair, obj.hair.frames[0])
    obj.imageHair.visible = true
  } else {
    obj.imageHair.visible = false
  }
  scale(obj, obj.scale)
}

export function moveToXY(
  obj: IVoyageBody,
  x: number,
  y: number,
  isFlipped: boolean,
) {
  obj.isFlipped = isFlipped

  obj.x = x
  obj.y = y
  obj.imageBody.x = x
  obj.imageBody.y = y
  obj.imageBody.rotation = obj.rotation

  let flipmult = 1
  if (obj.isFlipped) {
    flipmult = -1
  }

  obj.imageBody.flipX = obj.isFlipped
  obj.imageHead.flipX = obj.isFlipped
  obj.imageHair.flipX = obj.isFlipped

  let sc = obj.scale

  let x1 = 10 * sc * flipmult
  let y1 = -70 * sc

  obj.imageHead.x = x + mathUtils.rotX(x1, y1, obj.rotation)
  obj.imageHead.y = y + mathUtils.rotY(x1, y1, obj.rotation)
  obj.imageHead.rotation = obj.rotation

  let adj = obj.hair.adj[obj.head.idx]

  let x2 = 0 * sc * flipmult + adj.ox * flipmult * sc
  let y2 = -90 * sc + adj.oy * sc

  obj.imageHair.x = x + mathUtils.rotX(x2, y2, obj.rotation)
  obj.imageHair.y = y + mathUtils.rotY(x2, y2, obj.rotation)
  obj.imageHair.rotation = obj.rotation
}

export function kill(obj: IVoyageBody) {
  obj.isDead = true
}
export function killAll() {
  l.forEach(objs, (c) => {
    kill(c)
  })
  removeDead()
}

export function updateAll() {
  l.forEach(objs, (c) => {
    update(c)
  })
  removeDead()
}
export function removeDead() {
  entitiesUtils.removeDead(objs, (c) => {
    c.imageBody.destroy()
    c.imageHead.destroy()
    c.imageHair.destroy()
  })
}

function update(obj: IVoyageBody) {}

export function rotate(obj: IVoyageBody, rot = angleUtil.turn * 0.25) {
  obj.rotation = rot
  moveToXY(obj, obj.x, obj.y, obj.isFlipped)
}

export function scale(obj: IVoyageBody, scale: number) {
  obj.scale = scale

  let adj = obj.hair.adj[obj.head.idx]
  obj.imageBody.scale = 1 * scale
  obj.imageHead.scale = 0.75 * scale
  obj.imageHair.scaleX = 0.75 * scale * adj.sx
  obj.imageHair.scaleY = 0.75 * scale * adj.sy

  moveToXY(obj, obj.x, obj.y, obj.isFlipped)
}
